import React, { useCallback, useEffect } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import InfoCircle from '~/icons/components/InfoCircle';
import { DISCLOSURE_VIEWED_SESSION_DETAIL } from '~/shared/constants/tariff';
import {
  Tooltip,
  mobile,
  useBreakpoint,
} from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';
import {
  useSessionActions,
  useSessionDetail,
} from '~/techstyle-shared/redux-core';

const TariffCallout = styled.div`
  display: flex;
  color: ${({ theme: { colors } }) => colors.palette.shadow65};
  font-size: 12px;
  margin-top: 16px;
  ${({ tariffCalloutStyle }) => tariffCalloutStyle}
`;

const TariffLabel = styled.div``;

const TooltipAnchor = styled(Tooltip.Anchor)`
  width: 0;
`;

const TooltipIconWrapper = styled.div`
  width: 14px;
  height: 14px;
  margin-left: 4px;
  cursor: pointer;
  border-radius: 50%;
  transition: border 0.2s ease, background-color 0.2s ease;
  &:hover {
    outline: 1px solid ${({ theme }) => theme.colors.palette.shadow40};
    outline-offset: 3px;
  }
  ${mobile`
    margin-top: 1px;
  `}
`;

const TooltipTitle = styled.div`
  text-transform: uppercase;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.palette.shadow};
  margin-bottom: 8px;
  ${mobile`
    margin-bottom: 9px
  `};
`;

const TooltipMessage = styled(Tooltip.Content)`
  max-width: 250px;
  font-size: 12px;
  padding: 16px;
  border-radius: 8px;
  border: 0.5px solid ${({ theme: { colors } }) => colors.palette.shadow30};
  background: ${({ theme: { colors } }) => colors.palette.white};
  box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.05);
  z-index: 5;
  ${mobile`
    max-width: 330px;
  `};
`;

const TooltipBody = styled.div`
  margin-bottom: 12px;
  color: ${({ theme }) => theme.colors.palette.shadow};
  ${({ tariffBodyStyle }) => tariffBodyStyle}
`;

const TooltipFooter = styled.div`
  color: ${({ theme }) => theme.colors.palette.shadow65};
`;

export default function TariffTooltip({
  label,
  showFooter = true,
  showLabel = true,
  tariffBodyStyle,
  tariffCalloutStyle,
  mobileXOffset = 0,
}) {
  const { isMobile } = useBreakpoint();
  const sessionActions = useSessionActions();
  const { value: disclosureViewed } = useSessionDetail(
    DISCLOSURE_VIEWED_SESSION_DETAIL.NAME
  );

  const onShow = useCallback(() => {
    sessionActions.updateSessionDetail({
      name: DISCLOSURE_VIEWED_SESSION_DETAIL.NAME,
      value: DISCLOSURE_VIEWED_SESSION_DETAIL.VALUES.TOOLTIP_VISIBLE,
    });
  }, [sessionActions]);

  useEffect(() => {
    if (
      disclosureViewed !==
      DISCLOSURE_VIEWED_SESSION_DETAIL.VALUES.TOOLTIP_VISIBLE
    ) {
      sessionActions.updateSessionDetail({
        name: DISCLOSURE_VIEWED_SESSION_DETAIL.NAME,
        value: DISCLOSURE_VIEWED_SESSION_DETAIL.VALUES.DISCLOSURE_VISIBLE,
      });
    }
  }, [disclosureViewed, sessionActions]);

  return (
    <TariffCallout tariffCalloutStyle={tariffCalloutStyle}>
      {showLabel && (
        <TariffLabel>
          {label || (
            <FormattedMessage
              id="site_pdp.tariff_disclosure_disclaimer"
              defaultMessage="Pricing Excludes Tariff Surcharge"
            />
          )}
        </TariffLabel>
      )}
      <Tooltip
        xOffset={!isMobile ? 150 : mobileXOffset}
        yOffset={!isMobile ? -100 : ''}
        triggerOnClick
        onShow={onShow}
      >
        <TooltipAnchor>
          <TooltipIconWrapper>
            <InfoCircle size={14} />
          </TooltipIconWrapper>
        </TooltipAnchor>
        <TooltipMessage showCloseButton>
          <TooltipTitle>
            <FormattedMessage
              id="site_pdp.tariff_surcharge_disclosure_title"
              defaultMessage="Tariff Surcharge"
            />
          </TooltipTitle>
          <TooltipBody tariffBodyStyle={tariffBodyStyle}>
            <FormattedMessage
              id="site_pdp.cart_surcharge_tariff_disclosure_tooltip"
              defaultMessage="Recent changes to tariff policy have increased the cost of importing goods. To ensure the quality of apparel you've come to trust from Fabletics & Yitty, we've implemented a small percentage tariff surcharge. Thank you for your understanding and support as we navigate this industry-wide challenge together."
            />
          </TooltipBody>
          {showFooter && (
            <TooltipFooter>
              <FormattedMessage
                id="site_pdp.tariff_surcharge_disclosure_calculation"
                defaultMessage="This Tariff Surcharge will be calculated when viewing your cart"
              />
            </TooltipFooter>
          )}
        </TooltipMessage>
      </Tooltip>
    </TariffCallout>
  );
}

TariffTooltip.propTypes = {
  label: PropTypes.node,
  mobileXOffset: PropTypes.number,
  showFooter: PropTypes.bool,
  showLabel: PropTypes.bool,
  tariffBodyStyle: PropTypes.string,
  tariffCalloutStyle: PropTypes.string,
};
